"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { handleAddCart, getTotals } from "@/lib/store/Slices/CartSlice";
import { useAppDispatch } from "@/lib/store/hooks";
import { setCategory } from "@/lib/store/Slices/FilterSlice";
import { Popover, OverlayTrigger, Row, Col } from "react-bootstrap";
import { Button, Tag, Tooltip } from "antd";
import { Flame, Droplet } from "lucide-react";
import MakeOffer from "../MakeOffer/MakeOffer";
import Swal from "sweetalert2";
import { useRef } from "react";
import "./ProductCard.css";
import "../../app/globals.css";
import * as appConstants from "../../utils/constants";
import { isNewArrival } from "@/utils/dateUtils";
import Image from "next/image";

const ProductCard = (props) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [showOffer, setShowOffer] = useState(false);
  const [loginData, setLoginData] = useState({});
  const badgeRef = useRef(null);

  useEffect(() => {
    const storedLoginData = JSON.parse(localStorage.getItem("login")) || {};
    setLoginData(storedLoginData);
  }, []);

  const toggleShowOffer = () => {
    setShowOffer(!showOffer);
  };

  // console.log("Clarity User ID:", clarityUserId);

  const handleSwal = () => {
    Swal.fire({
      title: "Thanks for shopping with us",
      text: "Item added to cart successfully",
      icon: "success",
      showCancelButton: true,
      cancelButtonText: "Continue Shopping",
      confirmButtonColor: "#043289",
      cancelButtonColor: "#043289",
      confirmButtonText: "Go to Checkout",
    }).then((result) => {
      if (result.isConfirmed) {
        router.push("/shopping-cart");
      }
    });
  };

  // Frenzy Alert Badge with Popover instead of Tooltip
  const renderFrenzyAlertBadge = () => {
    const popover = (
      <Popover id="frenzy-alert-popover" className="popover-wide">
        <Popover.Body>
          <strong>
            🩸 Blood’s in the water. The feeding frenzy has begun act now or get
            left behind. <br></br>
          </strong>
          This item is gaining rapid attention. It’s priced aggressively,
          available in limited quantity, and moving quickly. First come, first
          served. Once it’s gone — it’s gone.
        </Popover.Body>
      </Popover>
    );

    return (
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement="bottom"
        overlay={popover}
        // delay={{ show: 250, hide: 400 }}
      >
        <div
          className="d-inline-flex align-items-center frenzy-badge"
          style={{
            height: "40px",
            padding: "0 12px",
            backgroundColor: "#fff",
            color: "black",
            padding: "5px",
            borderRadius: "4px",
            borderLeft: "4px solid red",
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            cursor: "help",
          }}
        >
          <Droplet size={20} color="red" fill="#fb1818" className="me-2" />

          <div className="d-flex flex-column">
            <span
              className="fw-bold"
              style={{ fontSize: "0.875rem", lineHeight: "1.2" }}
            >
              FRENZY ALERT
            </span>
            <span style={{ fontSize: "0.75rem", lineHeight: "1.2" }}>
              Blood in the Water
            </span>
          </div>
        </div>
      </OverlayTrigger>
    );
  };

  const addCart = (selectedItem) => {
    dispatch(handleAddCart(selectedItem));
    dispatch(getTotals());
    handleSwal();
    fetch("/api/AddToCartNotification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        itemName: selectedItem.description,
        clarityUserId: localStorage.getItem("clarity_user_id") || "N/A",
        name: loginData?.name || "Guest",
        qty: 1,
        userEmail: loginData?.email || "Guest",
        address: loginData?.address || "Guest",
        price: selectedItem.salePrice,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to send add-to-cart email");
        console.log("✅ Add-to-cart email sent");
      })
      .catch((err) => {
        console.error("❌ Error sending email:", err);
      });

    if (loginData) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "USD",
          value: selectedItem.salePrice,
          items: [
            {
              item_name: selectedItem.description,
              item_id: selectedItem.id,
              price: selectedItem.salePrice,
              item_brand: selectedItem.brandName,
              item_category: selectedItem.categoryName,
              item_list_name: "Inventory Detail Page",
              index: 0,
              quantity: 1,
            },
          ],
        },
      });
    }

    if (window.uetq) {
      window.uetq.push({
        ea: "AddToCart", // Event Action
        el: selectedItem.title, // Event Label (e.g., product name)
        ev: selectedItem.salePrice, // Event Value (e.g., product price)
      });
    }
  };

  const buyNow = (selectedItem) => {
    dispatch(handleAddCart(selectedItem));
    dispatch(getTotals());
    fetch("/api/AddToCartNotification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        itemName: selectedItem.description,
        clarityUserId: localStorage.getItem("clarity_user_id") || "N/A",
        name: loginData?.name || "Guest",
        qty: 1,
        userEmail: loginData?.email || "Guest",
        address: loginData?.address || "Guest",
        price: selectedItem.salePrice,
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to send add-to-cart email");
        console.log("✅ Add-to-cart email sent");
      })
      .catch((err) => {
        console.error("❌ Error sending email:", err);
      });
    if (window.uetq) {
      window.uetq.push({
        ea: "BuyNow", // Event Action
        el: selectedItem.title, // Event Label (e.g., product name)
        ev: selectedItem.salePrice, // Event Value (e.g., product price or revenue)
      });
    }
    router.push("/shopping-cart");
  };

  const navigateToDetailPage = (id) => {
    const targetUrl = `${id}`;
    if (window.innerWidth > 768) {
      // 768px is a common breakpoint for mobile devices
      window.open(targetUrl, "_blank");
    } else {
      router.push(targetUrl);
    }
  };

  const navigateToInventoryPage = () => {
    const selectItem = {
      id: props.productDetail.RelatedCategoryId,
      name: props.productDetail.categoryName,
    };
    dispatch(setCategory(selectItem));
    router.push("/Inventory");
  };

  const appriasedValueExists = () => {
    return props.productDetail.salePrice && props.productDetail.salePrice > 0;
  };

  const renderImage = () => {
    const { coverImageURL, description } = props.productDetail;
    if (coverImageURL) {
      const imgSrcRegex = /src="(.*?)"/;
      const match = coverImageURL.match(imgSrcRegex);
      const imageUrl = match ? match[1] : null;

      if (imageUrl) {
        return (
          <div
            className="img-fluid"
            style={{ height: "100%", position: "relative" }}
          >
            <Image
              src={imageUrl}
              alt={description || "Product Image"}
              // layout="fill"
              height={200}
              width={200}
              loading="lazy"
              objectFit="cover"
              className="productcard-img"
            />
            {isNewArrival(props.productDetail.createDate) && (
              <div className="new-arrival-overlay">New Arrival</div>
            )}
          </div>
        );
      }
    }
    return (
      <Image
        src="/assets/images/not-found.png"
        alt="No Image"
        // layout="fill"
        height={200}
        width={200}
        objectFit="cover"
      />
    );
  };

  const renderButtons = () => (
    <div className="ProductBtnWrap">
      {appriasedValueExists() && (
        <>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              buyNow(props.productDetail);
            }}
            className="btn btn-blue buynowButton"
            style={{ height: "40px", borderRadius: "5px" }}
          >
            Buy Now
          </a>
          <div className="productcard-button-container">
            <a
              href="#"
              className="btn btn-blue btnFullCart"
              style={{ height: "40px", borderRadius: "5px" }}
              onClick={(e) => {
                e.preventDefault();
                addCart(props.productDetail);
              }}
            >
              Add To Cart
            </a>
            <a
              href="#"
              onClick={toggleShowOffer}
              style={{ height: "40px", borderRadius: "5px" }}
              className="btn btn-blue btnFullBuyNow"
            >
              Make an Offer
            </a>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="ProductCardItem DesktopProduct ">
        <div
          className="ProductImg"
          onClick={() => navigateToDetailPage(props.productDetail.productSlug)}
        >
          {renderImage()}
          <div className="product-flex"></div>
        </div>

        <div className="ProductInfo">
          <a href={`https://kingsurplus.com${props.productDetail.productSlug}`}>
            <h5 className="title title_link">
              {props.productDetail.description}
            </h5>
          </a>
          {props.productDetail.isHotSale ? (
            <div style={{ marginBottom: "10px", marginTop: "10px" }}>
              {renderFrenzyAlertBadge()}
            </div>
          ) : (
            <h6 className="indsutryname-productcard">
              <a
                href={`https://kingsurplus.com${props.productDetail.productSlug}`}
              >
                {props.productDetail.industryName}
              </a>
            </h6>
          )}

          <Col>
            {props.productDetail.quantity > 0 && (
              <Tag color="green">
                Available Quantity :{" "}
                <strong>{props.productDetail.quantity}</strong>
              </Tag>
            )}

            {props.productDetail.isSpecializedFreightTransit === true && (
              <Tag color="blue">Specialized Freight Transit</Tag>
            )}
            {props.productDetail.isLocalPickupOnly === true && (
              <Tag color="blue">Pickup Only</Tag>
            )}
          </Col>
          {appriasedValueExists() ? (
            <h6 className="salePrice-productcard">
              ${appConstants.formattedValue(props.productDetail.salePrice)}
            </h6>
          ) : (
            <h5 className="my-5">{""}</h5>
          )}
          {renderButtons()}
        </div>
      </div>

      {showOffer && (
        <MakeOffer
          item={props.productDetail}
          close={toggleShowOffer}
          show={showOffer}
        />
      )}
    </>
  );
};

export default ProductCard;
